import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { requestPasswordReset, resetPassword as resetPasswordService } from '../services/resetPassword';
import { navigateToDashboard } from '../utils/roleNavigation';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/global.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, error: authError, user } = useAuth();
  const [error, setError] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query parameters for password reset
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');

    if (token) {
      setShowResetForm(true);
      setResetToken(token);
    }
  }, [location]);

  // If user is already logged in, redirect to original URL or appropriate dashboard
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname;
      if (from) {
        navigate(from, { replace: true });
      } else {
        navigateToDashboard(navigate, user.role || 'association');
      }
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const success = await login(email, password);

      if (!success) {
        setError(authError || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');

    if (!resetEmail) {
      setResetError('Inserisci un indirizzo email valido.');
      return;
    }

    try {
      const response = await requestPasswordReset(resetEmail);
      setResetMessage(response.message || 'Una nuova password è stata generata. Contatta l\'amministratore per ottenerla.');
      // Ora non è necessario passare al form di reset, la password è già stata reimpostata
    } catch (err) {
      setResetError(typeof err === 'string' ? err : 'Errore durante la richiesta di reset della password.');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');

    // Validate password
    if (!newPassword || newPassword.length < 6) {
      setResetError('La password deve essere di almeno 6 caratteri.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Le password non corrispondono.');
      return;
    }

    try {
      const response = await resetPasswordService(resetToken, newPassword);
      setResetMessage(response.message || 'Password reimpostata con successo! Ora puoi effettuare il login.');

      // Clear form and token from URL
      setShowResetForm(false);
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');

      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      setResetError(typeof err === 'string' ? err : 'Errore durante il reset della password. Il token potrebbe essere scaduto o non valido.');
    }
  };

  return (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
      <div className="glass-container p-4 rounded-4" style={{ maxWidth: '400px', width: '90%', backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
        {!showResetForm ? (
          <>
            {!showReset ? (
              <>
                <h2 className="text-center mb-4" style={{ color: '#006e39' }}>Login</h2>
                {error && <div className="alert alert-danger">{error}</div>}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email o Username</label>
                    <input
                      type="text"
                      className="form-control"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      style={{ borderColor: '#006e39' }}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{ borderColor: '#006e39' }}
                    />
                  </div>
                  <div className="d-grid gap-2">
                    <button type="submit" className="btn text-white" style={{ backgroundColor: '#006e39' }}>
                      Login
                    </button>
                    <button type="button" className="btn btn-link" style={{ color: '#006e39' }} onClick={() => setShowReset(true)}>
                      Password dimenticata?
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-center mb-4" style={{ color: '#006e39' }}>Recupera Password</h2>
                {resetMessage && <div className="alert alert-success">{resetMessage}</div>}
                {resetError && <div className="alert alert-danger">{resetError}</div>}
                <form onSubmit={handleRequestReset} className="d-flex flex-column gap-3">
                  <div className="mb-3">
                    <label htmlFor="resetEmail" className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      id="resetEmail"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      style={{ borderColor: '#006e39' }}
                      placeholder="Inserisci la tua email"
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn text-white" style={{ backgroundColor: '#006e39', flex: 1 }}>
                      Invia Link
                    </button>
                    <button type="button" className="btn btn-outline-secondary" style={{ flex: 1 }} onClick={() => setShowReset(false)}>
                      Indietro
                    </button>
                  </div>
                </form>
              </>
            )}
          </>
        ) : (
          <>
            <h2 className="text-center mb-4" style={{ color: '#006e39' }}>Reimposta Password</h2>
            {resetMessage && <div className="alert alert-success">{resetMessage}</div>}
            {resetError && <div className="alert alert-danger">{resetError}</div>}
            <form onSubmit={handlePasswordReset}>
              <div className="mb-3">
                <label htmlFor="newPassword" className="form-label">Nuova Password</label>
                <input
                  type="password"
                  className="form-control"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ borderColor: '#006e39' }}
                />
                <small className="form-text text-muted">La password deve essere di almeno 6 caratteri.</small>
              </div>
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">Conferma Password</label>
                <input
                  type="password"
                  className="form-control"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{ borderColor: '#006e39' }}
                />
              </div>
              <div className="d-grid gap-2">
                <button type="submit" className="btn text-white" style={{ backgroundColor: '#006e39' }}>
                  Reimposta Password
                </button>
                {resetMessage && (
                  <button type="button" className="btn btn-outline-secondary" onClick={() => {
                    setShowResetForm(false);
                    window.history.replaceState({}, document.title, window.location.pathname);
                  }}>
                    Torna al Login
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;